import { useState, type FormEvent } from 'react'

import type { InputAnswer, InputForm } from './protocol'
import styles from './InputRequestCard.module.scss'

type FieldType = 'string' | 'text' | 'number' | 'boolean' | 'select' | 'date'

type Field = {
  id: string
  label: string
  type: FieldType
  required: boolean
  options: { value: string; label: string }[]
}

type Outcome = Exclude<InputAnswer['outcome'], null>

const KIND_TITLES: Record<InputForm['kind'], string> = {
  question: 'Domanda',
  form: 'Modulo',
  approval: 'Conferma',
  url: 'Collegamento',
}

const OUTCOME_LABELS: Record<Outcome, string> = {
  submit: 'Risposta registrata',
  decline: 'Risposta saltata',
  cancel: 'Richiesta annullata',
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// The `form` kind lists its inputs in `schema.fields`; anything malformed is
// dropped rather than rendered as a broken control.
export function schemaFields(schema: Record<string, unknown>): Field[] {
  if (!Array.isArray(schema.fields)) return []
  return schema.fields.flatMap((raw): Field[] => {
    if (!isRecord(raw) || typeof raw.id !== 'string' || raw.id === '') return []
    const type = raw.type
    const known: FieldType[] = ['string', 'text', 'number', 'boolean', 'select', 'date']
    const options = Array.isArray(raw.options)
      ? raw.options.flatMap((option) => {
          if (typeof option === 'string' || typeof option === 'number') {
            return [{ value: String(option), label: String(option) }]
          }
          if (isRecord(option) && (typeof option.value === 'string' || typeof option.value === 'number')) {
            const value = String(option.value)
            return [{ value, label: typeof option.label === 'string' ? option.label : value }]
          }
          return []
        })
      : []
    return [{
      id: raw.id,
      label: typeof raw.label === 'string' && raw.label ? raw.label : raw.id,
      type: known.includes(type as FieldType) ? (type as FieldType) : 'string',
      required: raw.required === true,
      options,
    }]
  })
}

function fieldValues(fields: Field[], value: unknown): Record<string, unknown> {
  const stored = isRecord(value) ? value : {}
  return Object.fromEntries(fields.map((field) => [field.id, stored[field.id] ?? (field.type === 'boolean' ? false : '')]))
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value === null || value === undefined) return ''
  return JSON.stringify(value)
}

export function InputRequestCard({
  form,
  answer,
  readOnly,
  onRespond,
}: {
  form: InputForm
  answer?: InputAnswer
  readOnly: boolean
  onRespond: (outcome: Outcome, value: unknown) => Promise<boolean>
}) {
  const fields = form.kind === 'form' ? schemaFields(form.schema) : []
  const [question, setQuestion] = useState(() =>
    typeof answer?.value === 'string' ? answer.value : '',
  )
  const [values, setValues] = useState(() => fieldValues(fields, answer?.value))
  const [busy, setBusy] = useState(false)
  const locked = readOnly || answer?.frozen === true

  const respond = async (outcome: Outcome, value: unknown) => {
    if (busy || locked) return
    setBusy(true)
    try {
      await onRespond(outcome, value)
    } finally {
      setBusy(false)
    }
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (form.kind === 'question') void respond('submit', question)
    else if (form.kind === 'form') void respond('submit', values)
    else void respond('submit', true)
  }

  const setField = (field: Field, value: unknown) =>
    setValues((current) => ({ ...current, [field.id]: value }))

  const status = answer?.outcome ? OUTCOME_LABELS[answer.outcome] : null
  const answered =
    answer?.outcome === 'submit' && form.kind === 'question' ? formatValue(answer.value) : null

  return (
    <form
      className={styles.card}
      data-kind={form.kind}
      data-locked={locked || undefined}
      aria-label={form.title || KIND_TITLES[form.kind]}
      onSubmit={submit}
    >
      <p className={styles.title}>{form.title || KIND_TITLES[form.kind]}</p>
      {form.prompt ? <p className={styles.prompt}>{form.prompt}</p> : null}

      {form.kind === 'question' ? (
        locked ? (
          answered ? <p className={styles.answer}>{answered}</p> : null
        ) : (
          <textarea
            className={styles.textarea}
            value={question}
            rows={3}
            aria-label={form.title || KIND_TITLES.question}
            onChange={(event) => setQuestion(event.target.value)}
          />
        )
      ) : null}

      {form.kind === 'form' ? (
        <div className={styles.fields}>
          {fields.map((field) => (
            <label key={field.id} className={styles.field} data-type={field.type}>
              <span className={styles.label}>
                {field.label}
                {field.required ? ' *' : ''}
              </span>
              {locked ? (
                <span className={styles.answer}>{formatValue(values[field.id])}</span>
              ) : field.type === 'text' ? (
                <textarea
                  rows={3}
                  value={formatValue(values[field.id])}
                  required={field.required}
                  onChange={(event) => setField(field, event.target.value)}
                />
              ) : field.type === 'boolean' ? (
                <input
                  type="checkbox"
                  checked={values[field.id] === true}
                  onChange={(event) => setField(field, event.target.checked)}
                />
              ) : field.type === 'select' ? (
                <select
                  value={formatValue(values[field.id])}
                  required={field.required}
                  onChange={(event) => setField(field, event.target.value)}
                >
                  <option value="">—</option>
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                  value={formatValue(values[field.id])}
                  required={field.required}
                  onChange={(event) =>
                    setField(
                      field,
                      field.type === 'number' && event.target.value !== ''
                        ? Number(event.target.value)
                        : event.target.value,
                    )}
                />
              )}
            </label>
          ))}
        </div>
      ) : null}

      {form.kind === 'url' && form.url ? (
        <a className={styles.link} href={form.url} target="_blank" rel="noopener noreferrer">
          Apri il collegamento
        </a>
      ) : null}

      {status ? <p className={styles.status}>{status}</p> : null}

      {locked ? null : (
        <div className={styles.actions}>
          <button type="submit" className={styles.primary} disabled={busy}>
            {form.kind === 'approval' ? 'Approva' : form.kind === 'url' ? 'Fatto' : 'Invia'}
          </button>
          <button
            type="button"
            className={styles.secondary}
            disabled={busy}
            onClick={() => void respond('decline', null)}
          >
            {form.kind === 'approval' ? 'Rifiuta' : 'Salta'}
          </button>
        </div>
      )}
    </form>
  )
}
